import { authHeaders, fetchWithAuthRetry, getApiUrl, parseApiError } from './common';
import type { PagedData, PaginationMeta } from './common';

export interface PostMortem {
  id: string;
  projectId: string;
  planId: string | null;
  planTitle: string | null;
  title: string;
  content: string;
  actionItems: string[];
  status: string;
  contentTokenCount?: number | null;
  createdByMemberId?: string | null;
  createdByAgentId?: string | null;
  createdByType?: string | null;
  /** 경유 축. 도구 축을 특정하지 못해도 웹 작성물과 에이전트 표면 작성물을 구분한다. */
  createdByCredentialKind?: string | null;
  /** 작성에 사용된 도구(에이전트 설정) 이름. 행위 주체 축과 직교한다. */
  createdByAgentName?: string | null;
  createdByName?: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export type PostMortemListItem = Omit<PostMortem, 'content' | 'actionItems'>;

type PostMortemListResponse = {
  data: PostMortemListItem[];
};

type PostMortemPagedResponse = {
  data: PostMortemListItem[];
  meta: PaginationMeta;
};

type PostMortemResponse = {
  data: PostMortem;
};

export const fetchPostMortems = async (
  token: string,
  projectId: string,
  params?: {
    planId?: string;
    status?: string;
    createdByMemberId?: string;
  },
): Promise<PostMortemListItem[]> => {
  const queryParams = new URLSearchParams();
  if (params?.planId) queryParams.set('planId', params.planId);
  if (params?.status) queryParams.set('status', params.status);
  if (params?.createdByMemberId) queryParams.set('createdByMemberId', params.createdByMemberId);

  const url = `${getApiUrl()}/api/projects/${projectId}/post-mortems${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await fetchWithAuthRetry(url, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch post mortems: ${response.status}`);
  }

  const data = (await response.json()) as PostMortemListResponse;
  return data.data;
};

export const fetchPostMortemsPaged = async (
  token: string,
  projectId: string,
  params?: {
    search?: string;
    planId?: string;
    status?: string;
    createdByMemberId?: string;
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    pageSize?: number;
  },
): Promise<PagedData<PostMortemListItem>> => {
  const queryParams = new URLSearchParams();
  if (params?.search) queryParams.set('search', params.search);
  if (params?.planId) queryParams.set('planId', params.planId);
  if (params?.status) queryParams.set('status', params.status);
  if (params?.createdByMemberId) queryParams.set('createdByMemberId', params.createdByMemberId);
  if (params?.dateFrom) queryParams.set('dateFrom', params.dateFrom);
  if (params?.dateTo) queryParams.set('dateTo', params.dateTo);
  if (params?.page) queryParams.set('page', String(params.page));
  if (params?.pageSize) queryParams.set('pageSize', String(params.pageSize));

  const url = `${getApiUrl()}/api/projects/${projectId}/post-mortems${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await fetchWithAuthRetry(url, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch post mortems: ${response.status}`);
  }

  const data = (await response.json()) as PostMortemPagedResponse;
  return data;
};

export const fetchPostMortemById = async (token: string, projectId: string, id: string): Promise<PostMortem> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/post-mortems/${id}`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch post mortem: ${response.status}`);
  }

  const data = (await response.json()) as PostMortemResponse;
  return data.data;
};

export const updatePostMortem = async (
  token: string,
  projectId: string,
  id: string,
  input: Partial<Pick<PostMortem, 'planId' | 'title' | 'content' | 'actionItems' | 'status'>>,
): Promise<PostMortem> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/post-mortems/${id}`, {
    method: 'PUT',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to update post mortem: ${response.status}`);
  }

  const data = (await response.json()) as PostMortemResponse;
  return data.data;
};

export const deletePostMortem = async (token: string, projectId: string, id: string): Promise<void> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/post-mortems/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to delete post mortem: ${response.status}`);
  }
};
